import {
  useEffect,
  useMemo,
  useState
} from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import {
  Box,
  Container,
  Stack,
  Typography,
  Button,
  InputAdornment,
  TextField,
  useTheme
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search as SearchIcon,
  FileDownloadOutlined as DownloadIcon
} from '@mui/icons-material';
import FilterSelectInput from 'src/common/FilterSelectInput';
import { DATE_FORMAT } from 'src/utils/constants';
import ReportFilterModal from 'src/components/reports/ReportFilterModal';
import ReconciliationReportTable from 'src/components/reports/ReconciliationReportTable';
import { getAllDispensers } from 'src/redux/dispenserSlice';
import {
  reconciliationReportFormModel,
  reconciliationReportFormInitialValues,
  reconciliationReportValidationSchema
} from 'src/components/reports/reconciliationReportFormModel';
import API_URL from 'src/api/apiConstants';
import DateFilterField from 'src/common/formFields/DateFilterField';
import useAdminSiteFilter from 'src/hooks/useAdminSiteFilter';
import { getSelectedSiteFilterValue } from 'src/utils/getInitials';
import usePersistentListFilters from 'src/hooks/usePersistentListFilters';

const ReconciliationReport = ({ location }) => {
  const {
    search, site, date, setSearch, setSite, setDate
  } = usePersistentListFilters();
  const theme = useTheme();
  const dispatch = useDispatch();
  const {
    isAdmin,
    siteFilter,
    setSiteFilter,
    siteOptions
  } = useAdminSiteFilter(site);
  const { allDispensers } = useSelector((state) => state.dispensers);

  const [searchText, setSearchText] = useState(search);
  const [dispenserFilter, setDispenserFilter] = useState('all');
  const [openReportModal, setOpenReportModal] = useState(false);
  const [filterDate, setFilterDate] = useState(date || null);

  const downloadUrl = `${API_URL.reconciliationReport}?export=true`;

  useEffect(() => {
    if (allDispensers.length === 0) {
      dispatch(getAllDispensers());
    }
  }, [allDispensers.length, dispatch]);

  useEffect(() => {
    setDispenserFilter('all');
  }, [siteFilter]);

  const dispenserOptions = useMemo(() => {
    const selectedSite = getSelectedSiteFilterValue(siteFilter);
    const filteredDispensers = selectedSite
      ? allDispensers.filter((dispenser) => dispenser.site === selectedSite)
      : allDispensers;
    return [{ id: 'all', name: 'All Dispensers' }, ...filteredDispensers];
  }, [allDispensers, siteFilter]);

  const handleSearch = (e) => { setSearchText(e.target.value); setSearch(e.target.value); };

  const filterByDate = (newValue) => {
    const dateString = newValue
      ? newValue.format(DATE_FORMAT.unixDate)
      : undefined;
    setFilterDate(dateString);
    setDate(dateString || '');
  };

  const clearDateFilter = () => { setFilterDate(null); setDate(''); };

  return (
    <>
      <Helmet>
        <title>Reconciliation Report | Retrack FMS</title>
      </Helmet>
      <Box
        sx={{
          backgroundColor: '#FFFFFF',
          minHeight: 'calc(100vh - 64px)',
          height: { xs: 'auto', md: 'calc(100vh - 64px)' },
          overflow: { xs: 'auto', md: 'hidden' },
          py: 2
        }}
      >
        <Container
          maxWidth="xl"
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >

          {/* ✅ Unified Header */}
          <Stack
            direction={{ xs: 'column', lg: 'row' }}
            alignItems={{ xs: 'flex-start', lg: 'center' }}
            justifyContent="space-between"
            spacing={3}
            sx={{ py: 2, mb: 2, flexShrink: 0 }}
          >

            {/* Left Side - Title */}
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: '#111827',
                  letterSpacing: '-0.02em',
                  mb: 0.5
                }}
              >
                RECONCILIATION REPORT
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                Monitor daily fuel stock reconciliation across sites.
              </Typography>
            </Box>

            {/* Right Side - Filters + Actions */}
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent={{ xs: 'flex-start', sm: 'flex-end' }}
              useFlexGap
              flexWrap="nowrap"
              sx={{
                width: { xs: '100%', lg: 'auto' },
                overflowX: { xs: 'visible', sm: 'auto' },
                overflowY: 'hidden',
                pb: { xs: 0, sm: 0.5 },
                pr: { xs: 0, sm: 0.5 },
                '&::-webkit-scrollbar': {
                  height: 6
                },
                '&::-webkit-scrollbar-thumb': {
                  backgroundColor: '#CBD5E1',
                  borderRadius: 999
                }
              }}
            >

              {/* Date Filter */}

              {/* Search */}
              <TextField
                placeholder="Search Dispenser..."
                size="small"
                value={searchText}
                onChange={handleSearch}
                sx={{
                  width: {
                    xs: '100%', sm: 200, md: 220, xl: 260
                  },
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    backgroundColor: '#F9FAFB',
                    '& fieldset': { borderColor: '#E5E7EB' },
                    '&:hover fieldset': { borderColor: '#D1D5DB' },
                    '&.Mui-focused fieldset': {
                      borderColor: theme.palette.primary.main
                    }
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                    </InputAdornment>
                  )
                }}
              />

              <DateFilterField
                value={filterDate}
                onDateChange={filterByDate}
                onClearFilter={clearDateFilter}
              />

              {isAdmin && (
                <FilterSelectInput
                  label="Site"
                  name="site"
                  value={siteFilter}
                  data={siteOptions}
                  onInputChange={(value) => { setSiteFilter(value); setSite(value); }}
                  sx={{ width: { xs: '100%', sm: 160, md: 170 }, mr: 0 }}
                />
              )}

              <FilterSelectInput
                label="Dispenser"
                name="dispenser"
                value={dispenserFilter}
                data={dispenserOptions}
                onInputChange={setDispenserFilter}
                sx={{ width: { xs: '100%', sm: 180, md: 190 }, mr: 0 }}
              />

              {/* Export Button */}
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() => setOpenReportModal(true)}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 3,
                  width: { xs: '100%', sm: 'auto' },
                  minHeight: 40,
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                  backgroundColor: theme.palette.primary.main,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                    boxShadow: '0 6px 16px rgba(0,0,0,0.15)'
                  }
                }}
              >
                Export
              </Button>
            </Stack>
          </Stack>

          {/* Report Modal */}
          <ReportFilterModal
            open={openReportModal}
            title="Reconciliation Report"
            filterConfig={{ site: true, dispenser: true }}
            formModel={reconciliationReportFormModel}
            onlyDate
            formValidation={reconciliationReportValidationSchema}
            formInitValues={reconciliationReportFormInitialValues}
            downloadUrl={downloadUrl}
            handleClose={() => setOpenReportModal(false)}
          />

          {/* Table */}
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'hidden'
            }}
          >
            <ReconciliationReportTable
              refreshData={location.state?.fromDashboard}
              searchText={searchText}
              siteFilter={siteFilter}
              dispenserFilter={dispenserFilter}
              dateFilter={filterDate}
            />
          </Box>

        </Container>
      </Box>
    </>
  );
};

ReconciliationReport.propTypes = {
  location: PropTypes.object
};

export default ReconciliationReport;
